// src/modules/app-views/invoices/pages/Document/modals/ItemModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ShoppingCartOutlined, EditOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import financialService from 'services/financialService';
import fiscalService from 'services/fiscalService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import productStep from './Step1';
import taxReviewStep from './Step2';
import summaryStep from './Step3';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ItemModal = ({
  show,
  setShow,
  onConfirm,
  onClose,
  editingItem,
  storedCompany,
  issuer,
  documentType = 'nfe',
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!editingItem;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [calculatedTaxes, setCalculatedTaxes] = useState(null);
  const [zeroValueValidation, setZeroValueValidation] = useState(null);

  // Store original product fiscal data to detect changes
  const originalProductFiscalRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Reset state when modal opens/closes
  useEffect(() => {
    if (!show) {
      setSelectedProduct(null);
      setCalculatedTaxes(null);
      setZeroValueValidation(null);
      originalProductFiscalRef.current = null;
    } else if (editingItem) {
      // Pre-populate for editing
      setSelectedProduct({ _id: editingItem.productId, ...editingItem });
      setCalculatedTaxes(editingItem.taxes);
      // Store original fiscal data for change detection
      originalProductFiscalRef.current = {
        ncm: editingItem.ncm,
        cfop: editingItem.cfop,
        cest: editingItem.cest,
        icmsOrigin: editingItem.taxes?.icms?.origin,
        icmsCst: editingItem.taxes?.icms?.cst,
        icmsCsosn: editingItem.taxes?.icms?.csosn,
        icmsRate: editingItem.taxes?.icms?.rate,
        pisCst: editingItem.taxes?.pis?.cst,
        pisRate: editingItem.taxes?.pis?.rate,
        cofinsCst: editingItem.taxes?.cofins?.cst,
        cofinsRate: editingItem.taxes?.cofins?.rate,
        ipiCst: editingItem.taxes?.ipi?.cst,
        ipiRate: editingItem.taxes?.ipi?.rate,
      };
    }
  }, [show, editingItem]);

  // Capture original fiscal data when a new product is selected (for non-editing mode)
  useEffect(() => {
    if (selectedProduct && !editingItem && show) {
      // Store original fiscal data from the product for change detection
      const product = selectedProduct;
      const fiscalData = product.fiscalData || {};
      originalProductFiscalRef.current = {
        ncm: product.ncm || fiscalData.ncm || null,
        cfop: product.cfop || fiscalData.cfop || null,
        cest: product.cest || fiscalData.cest || null,
        icmsOrigin: product.icmsOrigin ?? fiscalData.icms?.origin ?? 0,
        icmsCst: product.icmsCst ?? fiscalData.icms?.cst,
        icmsCsosn: product.icmsCsosn ?? fiscalData.icms?.csosn,
        icmsRate: product.icmsRate ?? fiscalData.icms?.rate ?? 0,
        pisCst: product.pisCst ?? fiscalData.pis?.cst,
        pisRate: product.pisRate ?? fiscalData.pis?.rate ?? 0,
        cofinsCst: product.cofinsCst ?? fiscalData.cofins?.cst,
        cofinsRate: product.cofinsRate ?? fiscalData.cofins?.rate ?? 0,
        ipiCst: product.ipiCst ?? fiscalData.ipi?.cst,
        ipiRate: product.ipiRate ?? fiscalData.ipi?.rate ?? 0,
      };
    }
  }, [selectedProduct, editingItem, show]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(
    async ({ form }) => {
      const values = form.getFieldsValue(true);

      const quantity = values.quantity || 1;
      const unitValue = values.unitValue || 0;
      // Build discount object - use discount if available, otherwise create from discountValue
      const discount = values.discount || {
        init: 'value',
        percentage: values.discountPercentage || 0,
        value: values.discountValue || 0,
      };

      // Calculate subtotal and apply discount value
      const subtotal = financialService.roundCurrency(quantity * unitValue);
      // Use discount.value or fallback to discountValue at form level
      const discountValue = discount.value || values.discountValue || 0;
      const totalValue = financialService.roundCurrency(Math.max(0, subtotal - discountValue));

      // Get fiscal config from issuer (preferred) or company
      const fiscalConfigSource = issuer?.fiscalData || storedCompany?.fiscalData || {};

      // Determine tax regime for CST/CSOSN selection
      const taxRegime = fiscalConfigSource.taxRegime || 'simples_nacional';
      const isSimples = typeof taxRegime === 'string'
        ? taxRegime.includes('simples')
        : [1, 2, 4].includes(taxRegime);

      // Determine if this is an NFC-e document
      const isNfce = documentType === 'nfce';

      // Get product-specific fiscal data from form
      const productFiscal = {
        icmsOrigin: values.icmsOrigin ?? 0,
        icmsRate: values.icmsRate ?? 0,
        pisRate: values.pisRate ?? 0,
        cofinsRate: values.cofinsRate ?? 0,
        ipiRate: values.ipiRate ?? 0,
        // CST/CSOSN codes - use defaults if not provided
        icmsCst: values.icmsCst ?? fiscalConfigSource.defaultIcmsCst ?? '00',
        icmsCsosn: values.icmsCsosn ?? fiscalConfigSource.defaultIcmsCsosn ?? '102',
        pisCst: values.pisCst ?? fiscalConfigSource.defaultPisCst ?? '49',
        cofinsCst: values.cofinsCst ?? fiscalConfigSource.defaultCofinsCst ?? '49',
      };

      // Always recalculate taxes to ensure they match current totalValue and documentType
      const taxes = fiscalService.calculateItemTaxes({
        totalValue,
        fiscalConfig: fiscalConfigSource,
        productFiscal,
        documentType: documentType || 'nfe',
      });

      // Determine CFOP based on document type
      // For NFC-e: 5102 (resale) is generally accepted
      const cfop = values.cfop || determineCfop({ isSimples, storedCompany, isNfce });

      // Build item data with all required fiscal fields
      // The taxes object from fiscalService is already properly structured
      const itemData = {
        productId: values.productId,
        productCode: values.productCode,
        name: values.productName || values.description,
        description: values.description,
        ncm: values.ncm || null,
        cfop,
        cest: values.cest || null,
        unit: values.unit || 'UN',
        quantity,
        unitValue,
        // Discount as object: { init, percentage, value }
        discount,
        // Discount at item level (following backend structure)
        discountValue: discountValue,
        discountPercentage: discount.percentage || 0,
        totalValue,
        // Use the calculated taxes directly - fiscalService now returns clean structure
        // For NFC-e: no IPI, only csosn (not cst) for Simples Nacional
        taxes,
      };

      // ─────────────────────────────────────────────────────────────────────────
      // DETECT CHANGES TO FISCAL DATA AND UPDATE PRODUCT IF NEEDED
      // ─────────────────────────────────────────────────────────────────────────
      const productId = values.productId;
      const originalFiscal = originalProductFiscalRef.current;

      // Build current fiscal data to compare
      const currentFiscal = {
        ncm: values.ncm || null,
        cfop: cfop || null,
        cest: values.cest || null,
        icmsOrigin: values.icmsOrigin ?? 0,
        icmsCst: values.icmsCst,
        icmsCsosn: values.icmsCsosn,
        icmsRate: values.icmsRate ?? 0,
        pisCst: values.pisCst,
        pisRate: values.pisRate ?? 0,
        cofinsCst: values.cofinsCst,
        cofinsRate: values.cofinsRate ?? 0,
        ipiCst: values.ipiCst,
        ipiRate: values.ipiRate ?? 0,
      };

      // Check if any fiscal data changed
      const fiscalDataChanged = productId && originalFiscal && hasFiscalDataChanged(originalFiscal, currentFiscal);

      // If fiscal data changed, update the product in the database
      if (fiscalDataChanged) {
        try {
          const productUpdateData = {
            fiscalData: {
              ncm: currentFiscal.ncm,
              cfop: currentFiscal.cfop,
              cest: currentFiscal.cest,
              icms: {
                origin: currentFiscal.icmsOrigin,
                cst: currentFiscal.icmsCst,
                csosn: currentFiscal.icmsCsosn,
                rate: currentFiscal.icmsRate,
              },
              pis: {
                cst: currentFiscal.pisCst,
                rate: currentFiscal.pisRate,
              },
              cofins: {
                cst: currentFiscal.cofinsCst,
                rate: currentFiscal.cofinsRate,
              },
              ipi: {
                cst: currentFiscal.ipiCst,
                rate: currentFiscal.ipiRate,
              },
            },
          };

          // Update product fiscal data in the background
          const url = API.PRODUCTS.URL.replace(':_id?', productId);
          await axiosService.put({ url, data: productUpdateData });
        } catch (error) {
          // Log error but don't block the item addition
          console.error('[ItemModal] Failed to update product fiscal data:', error);
        }
      }

      onConfirm?.(itemData);
      return true;
    },
    [onConfirm, storedCompany, issuer, documentType]
  );

  const handleAfterClose = useCallback(() => {
    setSelectedProduct(null);
    setCalculatedTaxes(null);
    onClose?.();
  }, [onClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => {
    if (editingItem) {
      // Use fiscalConfig from issuer (preferred) or storedCompany for defaults
      const config = issuer?.fiscalData || storedCompany?.fiscalData || {};

      // Build discount object - use discount if available, otherwise create from discountValue
      const discountObj = editingItem.discount || {
        init: 'value',
        percentage: editingItem.discountPercentage || 0,
        value: editingItem.discountValue || 0,
      };

      const initVals = {
        productId: editingItem.productId,
        productCode: editingItem.productCode,
        description: editingItem.description,
        ncm: editingItem.ncm,
        cfop: editingItem.cfop,
        cest: editingItem.cest,
        unit: editingItem.unit || 'UN',
        quantity: editingItem.quantity || 1,
        unitValue: editingItem.unitValue || 0,
        discount: discountObj,
        totalValue: editingItem.totalValue || 0,
        // Fiscal data from item or taxes object
        icmsOrigin: editingItem.taxes?.icms?.origin ?? editingItem.icmsOrigin ?? 0,
        icmsCst: editingItem.taxes?.icms?.cst ?? config.defaultIcmsCst,
        icmsCsosn: editingItem.taxes?.icms?.csosn ?? config.defaultIcmsCsosn,
        icmsRate: editingItem.taxes?.icms?.rate ?? editingItem.icmsRate ?? 0,
        pisCst: editingItem.taxes?.pis?.cst ?? config.defaultPisCst,
        pisRate: editingItem.taxes?.pis?.rate ?? editingItem.pisRate ?? 0,
        cofinsCst: editingItem.taxes?.cofins?.cst ?? config.defaultCofinsCst,
        cofinsRate: editingItem.taxes?.cofins?.rate ?? editingItem.cofinsRate ?? 0,
        ipiCst: editingItem.taxes?.ipi?.cst,
        ipiRate: editingItem.taxes?.ipi?.rate ?? editingItem.ipiRate ?? 0,
        taxes: editingItem.taxes,
      };

      return initVals;
    }

    // Use fiscalConfig from issuer (preferred) or storedCompany
    const config = issuer?.fiscalData || storedCompany?.fiscalData || {};

    return {
      quantity: 1,
      unitValue: 0,
      discount: { init: 'percentage', percentage: 0, value: 0 },
      totalValue: 0,
      // Ensure NCM and CFOP are tracked by the form from the start
      ncm: null,
      cfop: null,
      cest: null,
      // Fiscal data defaults
      icmsOrigin: 0,
      icmsCst: config.defaultIcmsCst ?? '00',
      icmsCsosn: config.defaultIcmsCsosn ?? '102',
      icmsRate: config.defaultIcmsRate ?? 0,
      pisCst: config.defaultPisCst ?? '49',
      pisRate: config.defaultPisRate ?? 0,
      cofinsCst: config.defaultCofinsCst ?? '49',
      cofinsRate: config.defaultCofinsRate ?? 0,
      ipiRate: 0,
    };
  }, [editingItem, storedCompany, issuer]);

  // Determine if it's NFCe (always final consumer, simplified taxes)
  const isNfce = documentType === 'nfce';

  // Get fiscal config from issuer (preferred) or storedCompany (fallback)
  const fiscalConfig = issuer?.fiscalData || storedCompany?.fiscalData || {};

  const steps = useMemo(
    () => [
      // Step 1: Product selection
      productStep({
        selectedProduct,
        setSelectedProduct,
        storedCompany,
        issuer,
        documentType,
        isNfce,
        fiscalConfig,
        setCalculatedTaxes,
        zeroValueValidation,
        setZeroValueValidation,
      }),

      // Step 2: Tax review - allows editing fiscal fields and validates required data
      taxReviewStep({
        calculatedTaxes,
        setCalculatedTaxes,
        fiscalConfig,
        documentType,
        isNfce,
      }),

      // Step 3: Summary confirmation
      summaryStep({
        calculatedTaxes,
        isNfce,
      }),
    ],
    [selectedProduct, storedCompany, issuer, documentType, isNfce, fiscalConfig, calculatedTaxes, zeroValueValidation]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={isEditing ? 'edit_item' : 'add_item'}
      icon={isEditing ? <EditOutlined /> : <ShoppingCartOutlined />}
      steps={steps}
      onFinish={handleFinish}
      afterClose={handleAfterClose}
      width={720}
      initialValues={initialValues}
      initialStep={isEditing ? 0 : 0}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ItemModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Determine default CFOP based on company tax regime and operation type
 * @param {Object} params - Parameters
 * @param {boolean} params.isSimples - Is Simples Nacional regime
 * @param {Object} params.storedCompany - Company data
 * @param {boolean} params.isNfce - Is NFC-e document
 * @returns {string} Default CFOP code
 */
function determineCfop({ isSimples, storedCompany, isNfce }) {
  // Check if company has a default CFOP configured
  const defaultCfop = storedCompany?.fiscalData?.defaultCfop;
  if (defaultCfop) return defaultCfop;

  // Default CFOPs for internal sales (within state)
  // 5102 = Venda de mercadoria adquirida (resale) - most common for retail
  // 5101 = Venda de produção própria (own production)
  // Both are valid for NFC-e and NFe
  return '5102';
}

/**
 * Compare original and current fiscal data to detect changes
 * @param {Object} original - Original fiscal data from product
 * @param {Object} current - Current fiscal data from form
 * @returns {boolean} True if any fiscal field has changed
 */
function hasFiscalDataChanged(original, current) {
  if (!original) return !!current;

  // Compare each fiscal field
  const fieldsToCompare = [
    'ncm', 'cfop', 'cest',
    'icmsOrigin', 'icmsCst', 'icmsCsosn', 'icmsRate',
    'pisCst', 'pisRate',
    'cofinsCst', 'cofinsRate',
    'ipiCst', 'ipiRate',
  ];

  for (const field of fieldsToCompare) {
    const origValue = original[field];
    const currValue = current[field];

    // Handle null/undefined comparison
    const origNorm = origValue ?? null;
    const currNorm = currValue ?? null;

    if (origNorm !== currNorm) {
      return true;
    }
  }

  return false;
}
